import { useEffect, useRef } from "react";
import { TypeBadge } from "./TypeBadge";
import { DualTypeBadge } from "./DualTypeBadge";

interface HelpPageProps {
  onClose: () => void;
}

export function HelpPage({ onClose }: HelpPageProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  // Lock body scroll when help is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e: { key: string }) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  // Handle click outside to close
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (
      contentRef.current &&
      !contentRef.current.contains(e.target as HTMLElement)
    ) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div className="min-h-screen p-2 sm:p-4 md:p-8">
        <div
          ref={contentRef}
          className="max-w-4xl mx-auto bg-card rounded-lg shadow-lg border border-border p-4 sm:p-6 md:p-8"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold">
              How to Use the Type Chart
            </h1>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-80 transition-opacity"
            >
              Close
            </button>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {/* Main Chart Interactions */}
            <section>
              <h2 className="text-xl font-bold mb-4">
                Main Chart Interactions
              </h2>

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-2">
                    Clicking on a Row Background
                  </h3>
                  <p className="text-muted-foreground mb-3">
                    Click anywhere on a row to update the URL (e.g., /#Fire)
                    without scrolling. This makes it easy to share a link to a
                    specific type.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">
                    Clicking on Center Type Badges
                  </h3>
                  <p className="text-muted-foreground mb-3">
                    Click a center badge to view all dual type combinations for
                    that type.
                  </p>
                  <div className="bg-muted/30 rounded-lg p-3 sm:p-4 border border-border">
                    <div className="flex items-center justify-center gap-2 sm:gap-4">
                      <div className="text-xs sm:text-sm text-muted-foreground w-40 sm:w-48 text-right">
                        Click →
                      </div>
                      <TypeBadge
                        typeName="Fire"
                        className="text-xs sm:text-base px-2 sm:px-5 py-1 sm:py-3 min-w-[80px] sm:min-w-[120px] font-bold cursor-pointer"
                      />
                      <div className="text-xs sm:text-sm text-muted-foreground w-40 sm:w-48 text-left">
                        → View combinations
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">
                    Clicking on Attacking/Defending Badges
                  </h3>
                  <p className="text-muted-foreground mb-3">
                    Click any badge on the left (attacking) or right (defending)
                    side to scroll to that type&apos;s row with a visual
                    highlight.
                  </p>
                  <div className="bg-muted/30 rounded-lg p-3 sm:p-4 border border-border">
                    <div className="space-y-2">
                      <p className="text-xs sm:text-sm text-muted-foreground mb-2">
                        Example: After clicking Water from Fire&apos;s row
                      </p>
                      <div className="bg-card rounded-lg border-2 border-primary shadow-lg p-2 sm:p-3 transition-all duration-300 overflow-hidden">
                        <div className="flex items-center justify-center gap-1 sm:gap-2">
                          <div className="flex gap-1 justify-end flex-shrink-0">
                            <TypeBadge
                              typeName="Fire"
                              className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2 min-w-[60px] sm:min-w-[80px] scale-105 sm:scale-110 ring-1 sm:ring-2 ring-primary"
                              highlight
                            />
                          </div>
                          <div className="text-sm sm:text-lg text-muted-foreground flex-shrink-0">
                            →
                          </div>
                          <TypeBadge
                            typeName="Water"
                            className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2 min-w-[60px] sm:min-w-[80px] font-bold flex-shrink-0"
                          />
                          <div className="text-sm sm:text-lg text-muted-foreground flex-shrink-0">
                            →
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <TypeBadge
                              typeName="Fire"
                              className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2 min-w-[60px] sm:min-w-[80px] scale-105 sm:scale-110 ring-1 sm:ring-2 ring-primary"
                              highlight
                            />
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        The Water row gets a white border, scales up slightly,
                        and matching badges animate. This highlight clears after
                        2 seconds.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Dual Type View */}
            <section>
              <h2 className="text-xl font-bold mb-4">
                Dual Type Combinations View
              </h2>

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-2">
                    Accessing the View
                  </h3>
                  <p className="text-muted-foreground mb-3">
                    Click any center badge on the main chart to see that
                    type&apos;s combinations with all other types (e.g.,
                    Fire/Water, Fire/Grass, etc.)
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">
                    Understanding Dual Type Badges
                  </h3>
                  <p className="text-muted-foreground mb-3">
                    Dual type badges show both types with a diagonal split
                    background. The primary type colour is in the upper left,
                    and the secondary type colour is in the lower right.
                  </p>
                  <div className="bg-muted/30 rounded-lg p-3 sm:p-4 border border-border">
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
                      <DualTypeBadge
                        primaryType="Fire"
                        secondaryType="Water"
                        className="text-xs sm:text-base px-2 sm:px-5 py-1 sm:py-3 min-w-[80px] sm:min-w-[120px] font-bold"
                      />
                      <div className="text-xs sm:text-sm text-muted-foreground text-center">
                        Fire (top-left) / Water (bottom-right)
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">
                    Clicking on Dual Type Badges
                  </h3>
                  <p className="text-muted-foreground mb-3">
                    Click the center dual type badge to navigate to the main
                    chart and scroll to the secondary type&apos;s row. For
                    example, clicking Fire/Water takes you to Water.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">
                    Clicking on Attacking/Defending Badges
                  </h3>
                  <p className="text-muted-foreground mb-3">
                    Click any badge on the sides to return to the main chart and
                    scroll to that type&apos;s row.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">
                    Clicking on Rows
                  </h3>
                  <p className="text-muted-foreground mb-3">
                    Click the background of a combination row to update the URL
                    (e.g., /#Fire+Water) for easy sharing of specific
                    combinations.
                  </p>
                </div>
              </div>
            </section>

            {/* URL Behavior */}
            <section>
              <h2 className="text-xl font-bold mb-4">URL Sharing</h2>

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-2">URL Formats</h3>
                  <div className="bg-muted/30 rounded-lg p-4 border border-border space-y-2">
                    <div>
                      <code className="text-sm bg-muted px-2 py-1 rounded">
                        /#Fire
                      </code>
                      <p className="text-sm text-muted-foreground mt-1">
                        Links to the main chart, auto-scrolls to Fire&apos;s row
                      </p>
                    </div>
                    <div>
                      <code className="text-sm bg-muted px-2 py-1 rounded">
                        /#Fire+Fire
                      </code>
                      <p className="text-sm text-muted-foreground mt-1">
                        Opens Fire&apos;s dual type view showing all
                        combinations
                      </p>
                    </div>
                    <div>
                      <code className="text-sm bg-muted px-2 py-1 rounded">
                        /#Fire+Water
                      </code>
                      <p className="text-sm text-muted-foreground mt-1">
                        Opens Fire&apos;s dual type view, scrolls to Fire/Water
                        combination
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">
                    Shareable Links
                  </h3>
                  <p className="text-muted-foreground">
                    All URLs are shareable! Copy the URL from your
                    browser&apos;s address bar to share a specific type or
                    combination with others.
                  </p>
                </div>
              </div>
            </section>

            {/* Browser Navigation */}
            <section>
              <h2 className="text-xl font-bold mb-4">Browser Back/Forward</h2>

              <div className="space-y-4">
                <div>
                  <p className="text-muted-foreground mb-3">
                    The browser&apos;s back and forward buttons work as
                    expected:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>
                      Navigate between main chart and dual type views using back
                      button
                    </li>
                    <li>Return to previously viewed types by clicking back</li>
                    <li>
                      Use forward button to move through your navigation history
                    </li>
                    <li>
                      Each type or combination you view is saved in your browser
                      history
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Type Effectiveness */}
            <section>
              <h2 className="text-xl font-bold mb-4">
                Understanding Type Effectiveness
              </h2>

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-2">
                    Attacking (Left Side)
                  </h3>
                  <p className="text-muted-foreground mb-2">
                    Shows how attacks affect the center type:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                    <li>
                      <strong>Weak To (2×/4×)</strong>: Takes double or
                      quadruple damage
                    </li>
                    <li>
                      <strong>Resists (0.5×/0.25×)</strong>: Takes half or
                      quarter damage
                    </li>
                    <li>
                      <strong>Immune To (0×)</strong>: Takes no damage
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">
                    Defending (Right Side)
                  </h3>
                  <p className="text-muted-foreground mb-2">
                    Shows how the center type&apos;s attacks affect others:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                    <li>
                      <strong>Super Effective (2×)</strong>: Deals double damage
                    </li>
                    <li>
                      <strong>Not Very Effective (0.5×)</strong>: Deals half
                      damage
                    </li>
                    <li>
                      <strong>No Effect (0×)</strong>: Deals no damage
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">Badge Styles</h3>
                  <div className="bg-muted/30 rounded-lg p-3 sm:p-4 border border-border space-y-3">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                      <TypeBadge typeName="Fire" className="flex-shrink-0" />
                      <span className="text-xs sm:text-sm text-muted-foreground">
                        Solid background = Super effective / Weak to
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                      <TypeBadge
                        typeName="Water"
                        variant="text"
                        className="flex-shrink-0"
                      />
                      <span className="text-xs sm:text-sm text-muted-foreground">
                        Coloured text + border = Not very effective / Resists
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                      <TypeBadge
                        typeName="Grass"
                        variant="text"
                        strikethrough
                        className="flex-shrink-0"
                      />
                      <span className="text-xs sm:text-sm text-muted-foreground">
                        Coloured X strikethrough = No effect / Immune
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
